import { useState } from 'react';
import { FIELD_VALIDATIONS, VALIDATION_MESSAGES } from "../../../../../../utils/enums";

export const useContactValidation = (contactSegmentStore) => {
  const [validationErrors, setValidationErrors] = useState([]);
  const [filteredMappedFieldsGlobal, setFilteredMappedFieldsGlobal] = useState();
  
  const validateFields = () => {
    const errors = [];
    const parsedData = contactSegmentStore.parsedContactList;
    const systemFields = contactSegmentStore.defaultSystemFields;
    const fileFields = contactSegmentStore.defaultFileFields;
  
    if (!systemFields?.length || !fileFields?.length) {
      errors.push(VALIDATION_MESSAGES.NO_MAPPINGS);
      return errors;
    }
  
    if (!parsedData?.length) {
      errors.push(VALIDATION_MESSAGES.NO_DATA);
      return errors;
    }
  
    const mappedFields = fileFields.reduce((acc, fileField) => {
      const systemField = systemFields.find(sys => sys.index === fileField.index);
      if (systemField && fileField.value) {
        acc[systemField.mapping] = {
          csvColumn: fileField.value,
          displayName: systemField.name
        };
      }
      return acc;
    }, {});
  
    const columnsWithData = new Set();
    parsedData.forEach(row => {
      Object.keys(row).forEach(column => {
        if (row[column] && row[column].trim() !== '') {
          columnsWithData.add(column);
        }
      });
    });
  
    const filteredMappedFields = Object.keys(mappedFields).reduce((acc, fieldType) => {
      const fieldInfo = mappedFields[fieldType];
      if (columnsWithData.has(fieldInfo?.csvColumn)) {
        acc[fieldType] = fieldInfo;
      }
      return acc;
    }, {});
  
    if (!filteredMappedFields.email || !filteredMappedFields.first_name) {
      errors.push(VALIDATION_MESSAGES.FIRST_NAME_AND_EMAIL_REQUIRED);
    }
  
    const uniqueFieldErrors = new Map();
    const requiredFieldErrors = new Set();
    const duplicateEmails = new Set();
  
    const validRows = parsedData.filter(row => {
      return Object.values(row).some(value => value && value.trim && value.trim() !== '');
    });
  
    const emailSet = new Set();
  
    validRows.forEach((row) => {
      const emailValue = row[filteredMappedFields.email?.csvColumn];
  
      if (!emailValue || emailValue.trim() === "") {
        requiredFieldErrors.add(filteredMappedFields.email?.displayName);
      } else if (!FIELD_VALIDATIONS.email.pattern.test(emailValue)) {
        uniqueFieldErrors.set(filteredMappedFields.email?.displayName, {
          name: filteredMappedFields.email?.displayName,
          error: emailValue,
        });
      }
  
      const normalizedEmail = emailValue?.trim().toLowerCase();
      if (normalizedEmail) {
        if (emailSet.has(normalizedEmail)) {
          duplicateEmails.add(normalizedEmail);
        }
        emailSet.add(normalizedEmail);
      }
  
      Object.entries(filteredMappedFields).forEach(([fieldType, fieldInfo]) => {
        if (fieldType === 'email') return;
  
        const validation = FIELD_VALIDATIONS[fieldType];
        if (!validation) return;
  
        const value = row[fieldInfo?.csvColumn];
  
        if (value && value.trim() !== "" && validation.pattern && !validation.pattern.test(value)) {
          uniqueFieldErrors.set(fieldInfo?.displayName, {
            name: fieldInfo?.displayName,
            error: validation.error,
          });
        }
      });
    });

    if (duplicateEmails.size > 0) {
      errors.push({
        type: 'duplicated_emails',
        fields: Array.from(duplicateEmails)
      });
    }
  
    if (requiredFieldErrors.size > 0 && validRows.length > 0) {
      errors.push({
        type: 'required',
        fields: Array.from(requiredFieldErrors)
      });
    }
  
    if (uniqueFieldErrors.size > 0) {
      errors.push({
        type: 'invalid',
        fields: Array.from(uniqueFieldErrors.values())
      });
    }
    setFilteredMappedFieldsGlobal(filteredMappedFields)
    return errors;
  };

  const removeDuplicateEmails = () => {
    const emailColumn = contactSegmentStore.defaultFileFields.find(
      field => {
        const systemField = contactSegmentStore.defaultSystemFields.find(sys => sys.index === field.index);
        return systemField?.mapping === "email";
      }
    )?.value;
  
    if (!emailColumn) return;  
    const uniqueEmails = new Set();
    const cleanedData = contactSegmentStore.parsedContactList.filter(row => {
      const emailValue = row[emailColumn];

      if (!emailValue || emailValue.trim() === '') {
        return true;
      }
      
      const email = emailValue.trim().toLowerCase();
      if (uniqueEmails.has(email)) {
        return false;
      }
      uniqueEmails.add(email);
      return true;
    });
  
    contactSegmentStore.setParsedContactList([...cleanedData]);
    const newErrors = validateFields();
    const filteredErrors = newErrors.filter(error => error.type !== 'duplicated_emails');
    setValidationErrors(filteredErrors);
  };

  const clearValidationErrors = () => {
    setValidationErrors([]);
  };

  return {
    validationErrors,
    setValidationErrors,
    validateFields,
    clearValidationErrors,
    removeDuplicateEmails,
    filteredMappedFieldsGlobal
  };
};
